import { useRef, useState } from 'react'
import ErkennungsKarte, { vorauswahl } from '../komponenten/Erkennung'
import Gegenueberstellung from '../komponenten/Gegenueberstellung'
import Ladehinweis from '../komponenten/Ladehinweis'
import MenschKarte from '../komponenten/MenschKarte'
import NeuerOrt from '../komponenten/NeuerOrt'
import Stapel from '../komponenten/Stapel'
import { datum, punkteSatz, useLaden } from '../lib/laden'
import {
  GRUENDE_EINREICHUNG,
  einreichungAblehnen,
  einreichungAnnehmen,
  einreichungErkennungErneut,
  holeEinreichungErkennung,
  holeEinreichungen,
  type BildErkennung,
  type Einreichung,
  pflichtGrund,
} from '../lib/warteschlangen'

const ART_TEXT: Record<Einreichung['kind'], string> = {
  info: 'Ausstattung ergänzt',
  place_edit: 'Ort geändert',
  place_new: 'neuer Spielplatz',
  photo: 'Foto',
  review: 'Bewertung',
}

/** Warteschlange „Einreichungen" (§8.4). */
export default function Einreichungen() {
  const [zustand, neuLaden] = useLaden(holeEinreichungen)
  // Die angehakten Merkmale aus der Bilderkennung, je Einreichung. Die
  // Aktion liest sie beim Auslösen, nicht beim Rendern.
  const [wahl, setWahl] = useState<Record<string, string[]>>({})
  const wahlRef = useRef(wahl)
  wahlRef.current = wahl

  if (zustand.art !== 'da') return <Ladehinweis zustand={zustand} />

  // „Schon bekannt" ist hier, was der Einreicher selbst angegeben hat: Seine
  // Angabe schlägt die Erkennung — er war vor Ort, das Modell sah ein Bild.
  const merkmaleVon = (e: Einreichung): string[] =>
    wahlRef.current[e.id] ?? vorauswahl(e.bilder ?? [], e.vorschau.features)

  return (
    <Stapel<Einreichung>
      titel="Einreichungen"
      eintraege={zustand.wert.eintraege}
      gesamt={zustand.wert.gesamt}
      schluessel={(e) => e.id}
      neuLaden={neuLaden}
      gruende={GRUENDE_EINREICHUNG}
      leerText="Keine Einreichung wartet auf eine Entscheidung."
      freigebenText="Annehmen"
      zeile={(e) => (
        <>
          <span className="stapel-zeile-haupt">{ART_TEXT[e.kind] ?? e.kind}</span>
          <span className="leise">{datum(e.submitted_at)}</span>
        </>
      )}
      inhalt={(e) => (
        <EinreichungAnsicht
          e={e}
          gewaehlt={wahl[e.id] ?? vorauswahl(e.bilder ?? [], e.vorschau.features)}
          setzen={(m) => setWahl((w) => ({ ...w, [e.id]: m }))}
          // Genau einmal: Ein zweiter Lauf setzte sonst einen Haken zurück,
          // den jemand gerade bewusst entfernt hat.
          vorwaehlen={(bilder) =>
            setWahl((w) =>
              e.id in w ? w : { ...w, [e.id]: vorauswahl(bilder, e.vorschau.features) },
            )
          }
        />
      )}
      aktion={{
        freigeben: (e) => einreichungAnnehmen(e.id, merkmaleVon(e)),
        ablehnen: (e, code) => einreichungAblehnen(e.id, pflichtGrund(code)),
        bericht: (e, ergebnis, art) => {
          const name =
            e.mensch?.username ??
            e.mensch?.display_name ??
            `Anonym · ${e.user_firebase_uid.slice(0, 6)}`

          if (art === 'ab') {
            return `Einreichung abgelehnt · ${punkteSatz(ergebnis.punkte.differenz, name)}`
          }

          const bilder = ergebnis['bilder_angelegt']
          const ort = ergebnis['result_place_id']
          const teile = ['Einreichung angenommen']
          const merkmale = ergebnis['merkmale']
          if (Array.isArray(merkmale) && merkmale.length > 0) {
            teile.push(
              `${merkmale.length} ${merkmale.length === 1 ? 'Merkmal' : 'Merkmale'} übernommen`,
            )
          }
          // Der Name, wie er **jetzt am Ort steht** — zurückgelesen, nicht
          // das, was eingereicht war.
          const ortsname = ergebnis['ortsname']
          if (e.vorschau.name && typeof ortsname === 'string') {
            teile.push(`Ort heißt jetzt „${ortsname}"`)
          }
          if (typeof ort === 'string') teile.push('Ort angelegt')
          if (typeof bilder === 'number' && bilder > 0) {
            teile.push(`${bilder} ${bilder === 1 ? 'Bild' : 'Bilder'} übernommen`)
          }
          teile.push(punkteSatz(ergebnis.punkte.differenz, name))
          return teile.join(' · ')
        },
      }}
    />
  )
}

function EinreichungAnsicht({
  e,
  gewaehlt,
  setzen,
  vorwaehlen,
}: {
  e: Einreichung
  gewaehlt: string[]
  setzen: (schluessel: string[]) => void
  /** Nachgeladene Ergebnisse vorhaken — siehe `beiErgebnis` in der Karte. */
  vorwaehlen: (bilder: BildErkennung[]) => void
}) {
  return (
    <div className="ansicht">
      <div className="karte">
        <p className="marke">{ART_TEXT[e.kind] ?? e.kind}</p>
        <h2>{e.ort_name ?? (e.kind === 'place_new' ? 'Noch kein Ort' : 'Ort ohne Namen')}</h2>
        {e.place_id && (
          <p className="gedaempft">
            {[e.ort_strasse, [e.ort_plz, e.ort_stadt].filter(Boolean).join(' ')]
              .filter(Boolean)
              .join(', ') || 'keine Adresse hinterlegt'}
          </p>
        )}
        <p className="leise ansicht-zeile">Eingereicht: {datum(e.submitted_at)}</p>
      </div>

      {(e.kind === 'info' || e.kind === 'place_edit') && <Gegenueberstellung e={e} />}
      {e.kind === 'place_new' && <NeuerOrt e={e} />}

      {/* Die Bilder eines neuen Spielplatzes kommen nie in die Fotoschlange —
          deshalb steht die Erkennung hier. Und hier zählt sie am meisten: Ein
          neuer Ort hat nichts außer dem, was der Einreicher angibt. */}
      {e.kind === 'place_new' && (e.bilder?.length ?? 0) > 0 && (
        <ErkennungsKarte
          bilder={e.bilder}
          features={e.vorschau.features}
          umfeld={e.vorschau.accessibility}
          gewaehlt={gewaehlt}
          setzen={setzen}
          bekanntText="hat der Einreicher schon angegeben"
          widerspruchText={'der Einreicher sagt „gibt es hier nicht"'}
          nachladen={async () => (await holeEinreichungErkennung(e.id)).bilder}
          erneut={async () => {
            await einreichungErkennungErneut(e.id)
          }}
          beiErgebnis={vorwaehlen}
        />
      )}
      {(e.kind === 'photo' || e.kind === 'review') && (
        <div className="karte">
          <p className="gedaempft">
            Der Beitrag selbst hängt in der Warteschlange „
            {e.kind === 'photo' ? 'Fotos' : 'Bewertungstexte'}". Hier wird nur der
            Zustand der Einreichung gesetzt.
          </p>
        </div>
      )}

      <div className="karte">
        <MenschKarte mensch={e.mensch} uid={e.user_firebase_uid} />
      </div>
    </div>
  )
}
